"use client";

import { PaymentPointModel, PaymentPointModelFactory } from "@/core/models/payment-point.model";
import { PaymentPointRepo } from "@/core/repos/tables/payment-point.repo";
import { AddressRepo } from "@/core/repos/address.repo";
import container from "@/lib/di/container";
import { useState, useEffect } from "react";
import { FilterBar, FilterInputTemplate, FilterInputType, handleAddressChange } from "../../components/filter/FilterBar";
import { FilterSearchPayload, DataTable } from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { Button, message, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { DeleteButton } from "../../components/table/DeleteButton";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";

const paymentPointRepo = container.get(PaymentPointRepo);
const addressRepo = container.get(AddressRepo);

export default function PaymentPointPage() {
    const titleData = useTitleDataStore();
    const [modalData, setModalData] = useState<PaymentPointModel>(PaymentPointModelFactory.getEmpty());
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map());
    const [selectedRows, setSelectedRows] = useState<PaymentPointModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập tên/địa chỉ điểm thanh toán",
            type: FilterInputType.text,
        },
        {
            dataIndex: "status",
            label: "Trạng thái",
            placeholder: "Chọn trạng thái",
            type: FilterInputType.select,
            options: [
                { name: "Hoạt động", value: "Active" },
                { name: "Tạm ngưng", value: "Inactive" },
            ],
        },
        {
            dataIndex: "provinceId",
            label: "Tỉnh/ TP",
            placeholder: "Chọn Tỉnh/ TP",
            type: FilterInputType.select,
            options: []
        },
        {
            dataIndex: "districtId",
            label: "Quận/ Huyện",
            placeholder: "Chọn Quận/ Huyện",
            type: FilterInputType.select,
            options: []
        },
        {
            dataIndex: "wardId",
            label: "Xã/ Phường",
            placeholder: "Chọn Xã/ Phường",
            type: FilterInputType.select,
            options: []
        }
    ]);

    useEffect(() => {
        init();
    }, []);

    const populateAddressSelection = async () => {
        const provinces = (await addressRepo.getAllProvinces()).map(data => ({name: data[1].name, value: data[0]}));
        const templates: FilterInputTemplate[] = [...filterTemplates];
        const provinceTemplate = templates.find(template => template.dataIndex === "provinceId");
        if (provinceTemplate) {
            provinceTemplate.options = provinces;
        }
        setFilterTemplates(templates);
    };

    const init = async () => {
        titleData.setTitleActions({
            text: "Thêm điểm thanh toán",
            onPress: () => {
                showDetail(PaymentPointModelFactory.getEmpty());
            }
        });
        await populateAddressSelection();
    };

    async function onFilterChange(map: Map<string, FilterSearchPayload>, changes: string[]) {
        await handleAddressChange(map, changes, filterTemplates, setFilterTemplates);
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: PaymentPointModel[]) {
        setSelectedRows([...rows]);
    }

    const showDetail = (record: PaymentPointModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const deleteSelected = async () => {
        setDeleting(true);
        const result = await paymentPointRepo.deleteMany(selectedRows);
        messageApi.success({
            content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`
        });
        setDeleting(false);
    };

    return (
        <div className="px-4 py-6">
            {contextHolder}
            <FilterBar 
                setMap={setFilterMap} 
                onChange={onFilterChange} 
                templates={filterTemplates} 
                map={filterMap} 
                actions={[
                    <div key={1}>
                        <DeleteButton loading={deleting} length={selectedRows.length} onConfirm={deleteSelected} noGap/>
                    </div>
                ]}
            />
            <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
                <DataTable<PaymentPointModel>
                    columns={[
                        { dataIndex: 'name', name: 'Tên điểm thanh toán' },
                        { dataIndex: 'address', name: 'Địa chỉ' },
                        { dataIndex: 'phone', name: 'Số điện thoại' },
                        { dataIndex: 'status', name: 'Trạng thái' },
                        { dataIndex: 'createdAt', name: 'Ngày tạo', pipe: formatDateTimePipe },
                        { dataIndex: 'updatedAt', name: 'Ngày cập nhật', pipe: formatDateTimePipe },
                        {
                            name: 'Thao tác',
                            render: (_, record) => (
                                <div className="flex flex-row px-1 items-center justify-center">
                                    <Tooltip title="Sửa điểm thanh toán">
                                        <Button 
                                            type="text" 
                                            icon={<InfoCircleOutlined />} 
                                            onClick={() => showDetail(record)}
                                        />
                                    </Tooltip>
                                </div>
                            ),
                            fixed: "right"
                        },
                    ]}
                    repository={paymentPointRepo}
                    filterMap={filterMap}
                    onSelect={onRowSelect}
                ></DataTable>
            </div>
            <DetailModal 
                isModalOpen={isDetailModal} 
                setIsModalOpen={setDetailModal} 
                data={modalData}
            ></DetailModal>
        </div>
    );
}

